import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import config from './config';
import SidebarPageLayout from './SidebarPageLayout';
import useActiveUserId, { prepareSessionResume } from './useActiveUserId';

// "Care Sessions": start a new conversation or resume / rename / delete an
// earlier one. Moved out of Home so the home page stays simple.
function SessionsPage() {
  const navigate = useNavigate();
  const activeUserId = useActiveUserId();
  const [sessions, setSessions] = useState([]);
  // Session pending delete confirmation (null = dialog closed).
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Session pending rename (null = dialog closed).
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const fetchSessions = useCallback(async (userId) => {
    if (!userId) {
      setSessions([]);
      return;
    }

    try {
      const res = await axios.get(`${config.API_BASE_URL}/getallsessions`, {
        params: { userId },
      });
      setSessions(res.data || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, []);

  useEffect(() => {
    fetchSessions(activeUserId);
  }, [activeUserId, fetchSessions]);

  // Sessions start unnamed; the backend titles them from the caregiver's
  // first message.
  const handleCreateSession = async () => {
    try {
      const userId = activeUserId || localStorage.getItem('userId');
      if (!userId) {
        return;
      }
      const res = await axios.post(`${config.API_BASE_URL}/createsession`, { userId });

      localStorage.setItem('sessionId', res.data.id);
      localStorage.setItem('sessionIsNew', 'true');
      navigate('/chat');
    } catch (err) {
      console.error('Error creating session:', err);
    }
  };

  const handleRenameConfirm = async () => {
    const trimmedName = renameValue.trim();
    if (!trimmedName || !renameTarget) {
      return;
    }

    try {
      const userId = activeUserId || localStorage.getItem('userId');
      if (!userId) {
        return;
      }
      await axios.post(`${config.API_BASE_URL}/renamesession`, {
        userId,
        sessionId: renameTarget.id,
        name: trimmedName,
      });
      setSessions((prev) =>
        prev.map((session) =>
          session.id === renameTarget.id ? { ...session, name: trimmedName } : session
        )
      );
      setRenameTarget(null);
    } catch (err) {
      console.error('Error renaming session:', err);
    }
  };

  const handleDelete = async (sessionId) => {
    try {
      const userId = activeUserId || localStorage.getItem('userId');
      if (!userId) {
        return;
      }
      await axios.post(`${config.API_BASE_URL}/deletesession`, { sessionId, userId });
      setSessions((prev) => prev.filter((session) => session.id !== sessionId));
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const openSession = (sessionId) => {
    prepareSessionResume(sessionId);
    navigate('/chat');
  };

  const startRename = (session) => {
    setRenameTarget(session);
    setRenameValue(session.name || '');
  };

  return (
    <SidebarPageLayout>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="vica-title !text-[2.2rem]">Care Sessions</h1>
          <p className="vica-subtitle !text-[1.3rem] leading-8">
            Continue previous conversations or start a fresh session.
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-3">
          <button className="vica-btn-secondary vica-session-btn-new justify-center" onClick={() => navigate('/home')}>
            <ArrowBackIcon sx={{ fontSize: 28 }} />
            Back
          </button>
          <button className="vica-btn vica-session-btn-new justify-center" onClick={handleCreateSession}>
            <AddCircleOutlineIcon sx={{ fontSize: 28 }} />
            New Session
          </button>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="vica-empty-state !text-[1.25rem] leading-8">
          No sessions yet. Start a new conversation to begin tracking caregiver support.
        </div>
      ) : (
        <div className="vica-session-list">
          {sessions.map((session) => (
            <div className="vica-session-item" key={session.id}>
              <div className="vica-session-main">
                <div className="vica-session-name">{session.name || 'Untitled Session'}</div>
                <div className="vica-session-date">{new Date(session.createdAt).toLocaleString()}</div>
                <p className="vica-session-summary">
                  {session.summary || 'Summary will appear after conversation updates.'}
                </p>
              </div>
              <div className="vica-session-buttons">
                <button className="vica-btn vica-session-btn" onClick={() => openSession(session.id)}>
                  Resume
                </button>
                <button className="vica-btn-secondary vica-session-btn" onClick={() => startRename(session)}>
                  Rename
                </button>
                <button className="vica-btn-danger vica-session-btn" onClick={() => setDeleteTarget(session)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rename Session</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Session Name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button className="vica-btn-secondary vica-btn-md" onClick={() => setRenameTarget(null)}>
            Cancel
          </button>
          <button className="vica-btn vica-btn-md" onClick={handleRenameConfirm}>
            Save
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent>
          Are you sure you want to delete
          {' '}
          <strong>{deleteTarget?.name || 'Untitled Session'}</strong>
          ? This cannot be undone.
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button className="vica-btn-secondary vica-btn-md" onClick={() => setDeleteTarget(null)}>
            Cancel
          </button>
          <button
            className="vica-btn-danger vica-btn-md"
            onClick={() => {
              handleDelete(deleteTarget.id);
              setDeleteTarget(null);
            }}
          >
            Delete
          </button>
        </DialogActions>
      </Dialog>
    </SidebarPageLayout>
  );
}

export default SessionsPage;
